import React, { useState } from 'react';
import { AlertCircle, Sparkles, Loader2 } from 'lucide-react';
import Modal from '../components/Modal';
import ProcessingSteps from '../components/analysis/ProcessingSteps';
import ProcessingGraph from '../components/analysis/ProcessingGraph';
import FileUpload from '../components/analysis/FileUpload';
import RoleSelect from '../components/analysis/RoleSelect';
import AnalysisResult from '../components/analysis/AnalysisResult';
import PreAnalysisReview from '../components/analysis/PreAnalysisReview';
import ClarifyingQuestions from '../components/analysis/ClarifyingQuestions';
import { useAnalysisProcess } from '../hooks/useAnalysisProcess';

export default function NewAnalysis() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedMatter, setSelectedMatter] = useState<string>('');

  const {
    isProcessing,
    error,
    analysis,
    currentStep,
    steps,
    progress,
    processAnalysis,
    updateStep,
    resetAnalysis,
    preAnalysis,
    showPreAnalysisModal,
    confirmAnalysis,
    refineAnalysis,
    setShowPreAnalysisModal,
    isRefining,
    // Clarifying questions
    clarifyingQuestions,
    showQuestionsModal,
    setShowQuestionsModal,
    isSubmittingAnswers,
    questionsError,
    submitAnswersAndContinue,
  } = useAnalysisProcess();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedFile(file);
      updateStep(0, 'completed');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !selectedRole) {
      return;
    }

    await processAnalysis(selectedFile, selectedRole, selectedMatter);
  };

  const handleReset = () => {
    resetAnalysis();
    setSelectedFile(null);
    setSelectedRole('');
    setSelectedMatter('');
  };

  // Se tiver resultado, mostra o parecer gerado
  if (analysis) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white dark:bg-navy-900 rounded-xl shadow-lg border border-navy-200 dark:border-navy-700 p-6">
          <AnalysisResult
            content={analysis}
            onClose={handleReset}
            onReset={handleReset}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-navy-900 dark:text-navy-50">
          Novo Parecer
        </h1>
        <p className="text-navy-500 dark:text-navy-400 text-sm mt-1">
          Gere pareceres jurídicos automaticamente com IA
        </p>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-error-500/10 border border-error-500/30 text-error-600 dark:text-error-400 rounded-xl flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Form ou Processing */}
        <div className="space-y-6">
          {isProcessing && !showPreAnalysisModal && !showQuestionsModal ? (
            // Mostra indicador de processamento
            <div className="bg-white dark:bg-navy-900 rounded-xl shadow-lg border border-navy-200 dark:border-navy-700 p-8">
              <div className="flex flex-col items-center justify-center py-8">
                <div className="relative mb-6">
                  <div className="absolute inset-0 rounded-full bg-accent-400/20 blur-xl animate-pulse"></div>
                  <Loader2 className="h-14 w-14 text-accent-400 animate-spin relative" />
                </div>
                <p className="text-navy-600 dark:text-navy-300 text-center font-medium">
                  {currentStep === 1 && 'Enviando arquivo...'}
                  {currentStep === 2 && 'Carregando perguntas...'}
                  {currentStep === 3 && 'Analisando documento com IA...'}
                  {currentStep === 4 && 'Finalizando parecer...'}
                </p>
                <p className="text-navy-400 dark:text-navy-500 text-sm mt-2">
                  {currentStep === 4
                    ? 'Essa etapa pode demorar alguns minutos.'
                    : 'Por favor, aguarde'}
                </p>
              </div>
            </div>
          ) : (
            // Mostra formulário
            <form onSubmit={handleSubmit} className="bg-white dark:bg-navy-900 rounded-xl shadow-lg border border-navy-200 dark:border-navy-700 p-6">
              <FileUpload
                selectedFile={selectedFile}
                onFileChange={handleFileChange}
                isProcessing={isProcessing}
                onRemoveFile={() => setSelectedFile(null)}
              />

              <RoleSelect
                selectedRole={selectedRole}
                onChange={setSelectedRole}
                isProcessing={isProcessing}
                selectedMatter={selectedMatter}
                onMatterChange={setSelectedMatter}
              />

              <button
                type="submit"
                disabled={!selectedFile || !selectedRole || isProcessing}
                className="w-full btn-accent py-3 px-4 rounded-lg flex items-center justify-center space-x-2 disabled:bg-navy-300 disabled:text-navy-500 dark:disabled:bg-navy-700 dark:disabled:text-navy-500 disabled:cursor-not-allowed disabled:shadow-none transition-all duration-200"
              >
                <Sparkles className="h-5 w-5" />
                <span>Gerar Parecer</span>
              </button>
            </form>
          )}
        </div>

        {/* Right Column: Processing Status */}
        <div className="bg-white dark:bg-navy-900 rounded-xl shadow-lg border border-navy-200 dark:border-navy-700 p-6">
          <h2 className="text-lg font-semibold mb-4 text-navy-900 dark:text-navy-50">
            Status do Processamento
          </h2>

          <ProcessingSteps
            steps={steps}
            currentStep={currentStep}
          />

          {progress.length > 0 && (
            <div className="mt-6 pt-6 border-t border-navy-200 dark:border-navy-700">
              <h3 className="text-sm font-medium mb-3 text-navy-700 dark:text-navy-300">
                Progresso da Análise
              </h3>
              <ProcessingGraph data={progress} />
            </div>
          )}
        </div>
      </div>

      {/* Pre Analysis Data Confirmation Modal */}
      <Modal
        isOpen={showPreAnalysisModal}
        onClose={() => setShowPreAnalysisModal(false)}
        title="Pré-análise do Documento"
      >
        {preAnalysis && (
          <PreAnalysisReview
            content={preAnalysis}
            onConfirm={confirmAnalysis}
            onRefine={refineAnalysis}
            isRefining={isRefining}
          />
        )}
      </Modal>

      {/* Clarifying Questions Modal */}
      <Modal
        isOpen={showQuestionsModal}
        onClose={() => setShowQuestionsModal(false)}
        title="Perguntas Esclarecedoras"
        size="lg"
      >
        {clarifyingQuestions.length > 0 && (
          <ClarifyingQuestions
            questions={clarifyingQuestions}
            onSubmit={submitAnswersAndContinue}
            isSubmitting={isSubmittingAnswers}
            error={questionsError}
          />
        )}
      </Modal>
    </div>
  );
}
